import './services/firebaseApp'

import { Organisation } from '@/features'
import { I18NProvider, StyleProvider, useEffect } from '@codeleap/common'
import { ModalOutlet } from '@codeleap/modals'
import { Location, globalHistory } from '@reach/router'
import { QueryClientProvider } from '@tanstack/react-query'
import React from 'react'
import { Provider } from 'react-redux'
import { I18N, Settings, variantProvider } from './app'
import {
  AchievementDetail,
  AchievementModal,
  AchievementToast,
  AppStatusOverlay,
  CongratulationsStories,
  DebugButton,
  GlobalStyle,
  HereWeGoModal,
  ImpersonatingBanner,
  NoReviewQuestionsModal,
  OnboardingQuestions,
  PlayerYoutube,
  SignInNewsFeed,
  useOpenOnboardingQuestions,
  variants
} from './components'
import CreateStory from './components/Modals/CreateStory'
import StoryDetails from './components/Modals/StoryDetail'
import { ImmersiveVideo } from './components/Player/Immersive'
import { store } from './redux'
import { APIClient } from './services'
import { sessionReplayManager } from './services/analytics/amplitude'
import { analytics } from './services/analytics/segment'
import { useGetUTMSourcesFromURL, useHandleOpen, usePopulateReferralCodeFromURL } from './utils'
import { useAuthFlow } from './features/Auth/bridge/useAuthFlow'
import { useAutoLogin } from '@/features/Auth/bridge/useAuthState'

sessionReplayManager.initialize()

export const Overlays = () => (
  // This is needed for useLocation to work inside the modals
  <Location>
    {() => (
      <>
        <DebugButton />
        <Organisation.OrganisationInfo />
        <Organisation.UnknownOrgModal />
        <CreateStory />
        <NoReviewQuestionsModal />
        <StoryDetails />
        <OnboardingQuestions />
        <SignInNewsFeed />
        <CongratulationsStories />
        <AchievementDetail />
        <AchievementModal />
        <PlayerYoutube.Fullscreen />
        <HereWeGoModal />
        <AppStatusOverlay />
        <ModalOutlet />
        <ImpersonatingBanner />
      </>
    )}
  </Location>
)

const App = ({ children }) => {
  const authState = useAutoLogin()

  // Mounted exactly once. This is what turns a machine state into navigation, a modal or an
  // alert; two mounts would perform every transition twice. See EPIC-005.
  useAuthFlow()

  useHandleOpen({
    mapping: {
      openQuestions: undefined,
      questions: undefined,
      openCreateStory: 'createStory'
    },
    onOpen: () => Organisation.Pulse.ReviewOrganisationModal.toggle()
  })

  ImmersiveVideo.useCaptureFirstInteraction()
  usePopulateReferralCodeFromURL()
  useGetUTMSourcesFromURL()
  useOpenOnboardingQuestions(authState.isLoggedIn ? authState.profile : undefined)

  // `listen` returns its own unsubscribe — without returning it the effect stacks a new
  // listener on every re-run and never drops the old one, so one navigation reports as
  // several.
  useEffect(() => {
    return globalHistory.listen(({ action }) => {
      if (authState.isLoggedIn && action === 'PUSH') {
        analytics.track('logged_in_pageview')
      }
    })
  }, [globalHistory, authState.isLoggedIn])

  return (
    <>
      <GlobalStyle />
      {children}
      <Overlays />
    </>
  )
}

export const Root = ({ children }) => {
  return (
    <QueryClientProvider client={APIClient.queryClient.client}>
      <Provider store={store}>
        <StyleProvider settings={Settings} variantProvider={variantProvider} variants={variants} logger={logger}>
          <AchievementToast />

          <I18NProvider i18n={I18N}>
            <Location>
              {() => (
                <>
                  <App>{children}</App>
                </>
              )}
            </Location>
          </I18NProvider>
        </StyleProvider>
      </Provider>
    </QueryClientProvider>
  )
}
